/**
 * Gli allegati scelti **prima** che la scadenza esista.
 *
 * ⚠️ La rotta di caricamento è `POST /scadenze/:id/allegati`: finché la
 * scadenza non è salvata non c'è nessun id a cui appendere un file. In fase di
 * creazione i file si tengono quindi da parte in memoria e si spediscono
 * **subito dopo** che la scadenza è nata.
 *
 * Le decisioni stanno qui, fuori dal componente, perché il frontend non ha un
 * ambiente di prova React e questo è l'unico modo di averle coperte davvero.
 */
import type { RegoleAllegati } from '../api/tipi-dati.ts';

/** Perché un file non si può aggiungere, o `null` se si può. */
export function motivoRifiuto(
  file: { name: string; size: number },
  giaScelti: readonly { name: string }[],
  regole: RegoleAllegati | null,
): string | null {
  if (giaScelti.some((f) => f.name === file.name)) {
    return `«${file.name}» è già in elenco.`;
  }
  if (regole === null) return null;
  if (file.size > regole.massimoByte) {
    return `«${file.name}» supera il limite di ${regole.massimoLeggibile}.`;
  }
  // 0 = nessun tetto. Il confronto è con la lista *dopo* l'aggiunta, quindi
  // `>=`: con il tetto a 3 e tre file già scelti, il quarto non entra.
  if (regole.maxPerScadenza > 0 && giaScelti.length >= regole.maxPerScadenza) {
    return `Si possono allegare al massimo ${regole.maxPerScadenza} file per scadenza.`;
  }
  return null;
}

/**
 * Cosa dire dopo aver creato la scadenza e spedito gli allegati.
 *
 * 🔴 **La scadenza è nata comunque**, anche se qualche allegato non è passato:
 * annunciarla come un errore la farebbe cercare invano, e annunciarla come una
 * riuscita piena farebbe credere che i file ci siano. Si dice tutto e due, e i
 * nomi di quelli mancati per esteso — sono gli unici che vanno ricaricati.
 */
export function messaggioCreazione(
  descrizioneScadenza: string,
  scelti: number,
  falliti: readonly string[],
): string {
  const testa = `Creata la scadenza ${descrizioneScadenza}.`;
  if (scelti === 0) return testa;
  if (falliti.length === 0) {
    return `${testa} ${scelti === 1 ? 'Allegato caricato' : `${scelti} allegati caricati`}.`;
  }
  const riusciti = scelti - falliti.length;
  const parteRiuscita =
    riusciti === 0 ? 'Nessun allegato è stato caricato' : `${riusciti} su ${scelti} allegati caricati`;
  return `${testa} ⚠️ ${parteRiuscita}. Non è passato: ${falliti.join(', ')}. La scadenza c'è: riprova ad allegarli dalla sua scheda.`;
}
